import _slicedToArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { useRef, useState } from "react";
import { Popover, UnstyledButton } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createTextNode, $getRoot, $getSelection, $isRangeSelection, $setSelection } from "lexical";
import { $createLinkNode } from "@lexical/link";
import { useTranslation } from "react-i18next";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { Button } from "@/components/button";
import { FormInput, FormSelect } from "@/components/form";
import { MODAL_Z_INDEX } from "@/components/modal";
/** Toolbar button + popover that inserts a web link, phone (tel:), or email (mailto:) at the cursor. */
export function LinkInsertButton() {
  var _useLexicalComposerCo = useLexicalComposerContext(),
    _useLexicalComposerCo2 = _slicedToArray(_useLexicalComposerCo, 1),
    editor = _useLexicalComposerCo2[0];
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(false),
    opened = _useState[0],
    setOpened = _useState[1];
  var _useState2 = useState("url"),
    kind = _useState2[0],
    setKind = _useState2[1];
  var _useState3 = useState(""),
    text = _useState3[0],
    setText = _useState3[1];
  var _useState4 = useState(""),
    value = _useState4[0],
    setValue = _useState4[1];
  var savedSelection = useRef(null);
  var toggle = function toggle() {
    setOpened(function (current) {
      if (!current) {
        editor.getEditorState().read(function () {
          var selection = $getSelection();
          savedSelection.current = selection ? selection.clone() : null;
        });
      }
      return !current;
    });
  };
  var kindOptions = [{
    value: "url",
    label: t("link_kind_web", {
      defaultValue: "Web link"
    })
  }, {
    value: "phone",
    label: t("link_kind_phone", {
      defaultValue: "Phone number"
    })
  }, {
    value: "email",
    label: t("link_kind_email", {
      defaultValue: "Email"
    })
  }];
  var valuePlaceholder = kind === "phone" ? "(470) 793-4025" : kind === "email" ? "name@example.com" : "https://example.com";
  var buildHref = function buildHref(raw) {
    var trimmed = raw.trim();
    if (!trimmed) return "";
    if (kind === "phone") return "tel:".concat(trimmed.replace(/[^\d+]/g, ""));
    if (kind === "email") return "mailto:".concat(trimmed);
    if (/^(https?:|tel:|mailto:)/i.test(trimmed)) return trimmed;
    return "https://".concat(trimmed);
  };
  var reset = function reset() {
    setText("");
    setValue("");
    setKind("url");
    setOpened(false);
  };
  var insert = function insert() {
    var href = buildHref(value);
    if (!href) return;
    trackEvent(ANALYTICS_EVENTS.LETTER_LINK);
    editor.update(function () {
      var saved = savedSelection.current;
      if (saved) $setSelection(saved.clone());else if (!$isRangeSelection($getSelection())) $getRoot().selectEnd();
      var link = $createLinkNode(href, {
        rel: "noreferrer"
      });
      link.append($createTextNode(text.trim() || value.trim()));
      var selection = $getSelection();
      if ($isRangeSelection(selection)) selection.insertNodes([link]);
    });
    reset();
    editor.focus();
  };
  return /*#__PURE__*/React.createElement(Popover, {
    opened: opened,
    onChange: setOpened,
    position: "bottom-end",
    withinPortal: true,
    zIndex: MODAL_Z_INDEX + 100,
    shadow: "md",
    radius: "md"
  }, /*#__PURE__*/React.createElement(Popover.Target, null, /*#__PURE__*/React.createElement(UnstyledButton, {
    type: "button",
    "aria-label": t("insert_link", {
      defaultValue: "Insert link"
    }),
    title: t("insert_link", {
      defaultValue: "Insert link"
    }),
    "data-testid": "letter-link",
    onMouseDown: function onMouseDown(event) {
      return event.preventDefault();
    },
    onClick: toggle,
    className: "flex h-7 w-6 flex-shrink-0 items-center justify-center rounded-sds-200 text-sds-base-secondary transition-colors hover:bg-sds-base-secondary hover:text-sds-base-default"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faLink,
    className: "h-3.5 w-3.5"
  }))), /*#__PURE__*/React.createElement(Popover.Dropdown, {
    className: "w-72 border border-sds-base-default bg-sds-base-default p-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-3"
  }, /*#__PURE__*/React.createElement("p", {
    className: "text-style-body-strong text-sds-base-default"
  }, t("insert_link", {
    defaultValue: "Insert link"
  })), /*#__PURE__*/React.createElement(FormSelect, {
    data: kindOptions,
    value: kind,
    onChange: function onChange(next) {
      var _ref;
      return setKind((_ref = next) !== null && _ref !== void 0 ? _ref : "url");
    },
    withinPortal: false,
    "aria-label": t("link_kind", {
      defaultValue: "Link type"
    }),
    "data-testid": "letter-link-kind"
  }), /*#__PURE__*/React.createElement(FormInput, {
    placeholder: t("link_text_placeholder", {
      defaultValue: "Text to display (e.g. Call to schedule)"
    }),
    value: text,
    onChange: function onChange(event) {
      return setText(event.currentTarget.value);
    },
    "data-testid": "letter-link-text",
    "aria-label": t("link_text", {
      defaultValue: "Text to display"
    })
  }), /*#__PURE__*/React.createElement(FormInput, {
    placeholder: valuePlaceholder,
    value: value,
    inputMode: kind === "phone" ? "tel" : "text",
    onChange: function onChange(event) {
      return setValue(event.currentTarget.value);
    },
    onKeyDown: function onKeyDown(event) {
      return event.key === "Enter" && insert();
    },
    "data-testid": "letter-link-url",
    "aria-label": t("link_value", {
      defaultValue: "Link destination"
    })
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-end gap-2"
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "ghost",
    size: "sm",
    "data-testid": "letter-link-cancel",
    onClick: reset
  }, t("cancel", {
    defaultValue: "Cancel"
  })), /*#__PURE__*/React.createElement(Button, {
    size: "sm",
    "data-testid": "letter-link-insert",
    disabled: !value.trim(),
    onClick: insert
  }, t("insert", {
    defaultValue: "Insert"
  }))))));
}